import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  FormGroup,
} from "reactstrap";
import {
  getNextReceiptNumber,
  getCustomerCreditInvoices,
  saveReceipt,
} from "../../components/Api/GmsReceipt";
import { checkDayClosing } from "../../components/Api/GmsDayClosing";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { searchCustomers } from "../../components/Api/GmsCustomer";
import { User, FileText, Search } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { validateFile } from "../../utils/fileUploadHelper";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";
import CustomerProfileModal from "../../components/CustomerProfileModal";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const defaultForm = {
  ReceiptId: 0,
  ReceiptNumber: "",
  ReceiptDate: new Date(),
  Amount: 0,
  PaymentType: "ca",
  AuthCode: "",
  Type: "cu",
  Is_Advance: false,
  BusinessId: null,
  BranchId: null,
  CustomerId: null,
  CreatedBy: 1,
  ReceiptDetails: [],
  Receipt_Document: null,
  Receipt_DocumentPath: null,
};

export default function AddReceiptPage() {
  const [form, setForm] = useState(defaultForm);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [creditInvoices, setCreditInvoices] = useState([]);
  const [paymentAllocation, setPaymentAllocation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddReceipt');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsAddReceipt');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddReceipt');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Check if business/branch should be readonly
  const isBusinessReadonly = !!session.companyId;
  const isBranchReadonly = !!session.branchId;

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();

      // Set business/branch from session if available
      if (session.companyId) {
        setForm(prev => ({ ...prev, BusinessId: session.companyId }));
      }
      if (session.branchId) {
        setForm(prev => ({ ...prev, BranchId: session.branchId }));
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Check day closing after business/branch is set from session
  useEffect(() => {
    if (hasPermission) {
      // Small delay to ensure form state is updated
      const timer = setTimeout(() => {
        // Day closing validation will trigger when date is selected
        if (form.ReceiptDate) {
          checkSelectedDateDayClosing(form.ReceiptDate);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [hasPermission, session.companyId, session.branchId]);

  // Check if current date is closed/holiday and previous day closing
  // Check day closing for selected date (triggered on date selection)
  const checkSelectedDateDayClosing = async (selectedDate) => {
    try {
      if (!selectedDate) return;

      // Only check if we have business info
      const businessId = session.companyId || form.BusinessId;
      const branchId = session.branchId || form.BranchId || null;

      if (!businessId) {
        // Business not selected yet, skip validation
        return;
      }

      // Format selected date
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);

      // Check selected date with previous days check enabled (stricter validation)
      const checkRequest = {
        transactionDate: formatDateForAPI(selectedDateOnly, false),
        businessId: businessId,
        branchId: branchId,
        checkPreviousDays: true  // Enable stricter validation
      };

      const dayClosingCheck = await checkDayClosing(checkRequest);

      // Check if selected date itself is closed
      if (dayClosingCheck.isClosed) {
        const message = dayClosingCheck.isHoliday
          ? `Holiday has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`
          : `Day closing has been marked for ${formatDateForDisplay(selectedDateOnly, 'date')}. Please select a different date.`;

        Swal.fire({
          icon: 'warning',
          title: 'Day Closed',
          text: message,
          confirmButtonText: 'OK'
        });

        // Disable the form
        setIsReadOnly(true);
        return;
      }

      // Check if any previous days are not closed (stricter validation)
      if (dayClosingCheck.firstUnclosedDate) {
        const unclosedDateStr = formatDateForDisplay(new Date(dayClosingCheck.firstUnclosedDate), 'date');
        const selectedDateStr = formatDateForDisplay(selectedDateOnly, 'date');

        Swal.fire({
          icon: 'warning',
          title: 'Previous Day Not Closed',
          text: `Day closing is not done for ${unclosedDateStr}. Please close that day first before creating transactions for ${selectedDateStr}.`,
          confirmButtonText: 'OK'
        });

        // Disable the form
        setIsReadOnly(true);
        return;
      }

      // All checks passed - enable the form
      setIsReadOnly(false);

    } catch (error) {
      console.error("Error checking day closing for selected date:", error);
      // Don't block on error - allow user to continue
    }
  };

  // Check day closing when date is selected (NEW: validation triggers on date selection)
  useEffect(() => {
    if (form.ReceiptDate && form.BusinessId) {
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        checkSelectedDateDayClosing(form.ReceiptDate);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [form.ReceiptDate, form.BusinessId, form.BranchId]);

  // Load receipt number when business is selected
  useEffect(() => {
    if (form.BusinessId) {
      loadNextReceiptNumber();
    }
  }, [form.BusinessId]);

  // Load credit invoices when customer is selected
  useEffect(() => {
    console.log('🔄 [AddReceipt] useEffect triggered for credit invoices:', {
      CustomerId: form.CustomerId,
      BusinessId: form.BusinessId,
      BranchId: form.BranchId,
      hasCustomerId: !!form.CustomerId,
      hasBusinessId: !!form.BusinessId,
      willLoadInvoices: !!(form.CustomerId && form.BusinessId)
    });

    if (form.CustomerId && form.BusinessId) {
      console.log('✅ [AddReceipt] Conditions met, calling loadCreditInvoices');
      loadCreditInvoices();
    } else {
      console.log('⚠️ [AddReceipt] Conditions not met, clearing credit invoices:', {
        reason: !form.CustomerId ? 'No CustomerId' : 'No BusinessId'
      });
      setCreditInvoices([]);
      setPaymentAllocation([]);
    }
  }, [form.CustomerId, form.BusinessId, form.BranchId]);

  // Auto-allocate payment when amount changes
  useEffect(() => {
    if (form.Amount > 0 && creditInvoices.length > 0) {
      allocatePayment(form.Amount);
    } else {
      setPaymentAllocation([]);
    }
  }, [form.Amount, creditInvoices]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadBusinesses(),
        loadBranches(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadNextReceiptNumber = async () => {
    try {
      const response = await getNextReceiptNumber(form.BusinessId, "cu");
      if (response.isSuccess) {
        setForm(prev => ({ ...prev, ReceiptNumber: response.nextReceiptNumber }));
      }
    } catch (error) {
      console.error("Error loading next receipt number:", error);
    }
  };

  const loadCreditInvoices = async () => {
    console.log('🔍 [AddReceipt] loadCreditInvoices called');
    console.log('🔍 [AddReceipt] Parameters:', {
      CustomerId: form.CustomerId,
      BusinessId: form.BusinessId,
      BranchId: form.BranchId,
      CustomerIdType: typeof form.CustomerId,
      BusinessIdType: typeof form.BusinessId,
      BranchIdType: typeof form.BranchId
    });

    // Check if CustomerId is valid
    if (!form.CustomerId) {
      console.warn('⚠️ [AddReceipt] CustomerId is null/undefined, skipping credit invoices load');
      setCreditInvoices([]);
      return;
    }

    try {
      setLoading(true);
      console.log('📡 [AddReceipt] Calling getCustomerCreditInvoices API...');

      const invoices = await getCustomerCreditInvoices(
        form.CustomerId,
        form.BusinessId,
        form.BranchId
      );

      console.log('✅ [AddReceipt] getCustomerCreditInvoices API response:', {
        invoices,
        invoiceCount: invoices?.length || 0,
        isArray: Array.isArray(invoices),
        invoiceType: typeof invoices
      });

      // Log first invoice structure if available
      if (invoices && invoices.length > 0) {
        console.log('📋 [AddReceipt] First invoice structure:', invoices[0]);
      }

      setCreditInvoices(invoices || []);
      console.log('✅ [AddReceipt] Credit invoices loaded successfully:', (invoices || []).length, 'invoices');
    } catch (error) {
      console.error('❌ [AddReceipt] Error loading credit invoices:', error);
      console.error('❌ [AddReceipt] Error details:', {
        message: error?.message,
        stack: error?.stack,
        response: error?.response,
        responseData: error?.response?.data,
        responseStatus: error?.response?.status,
        responseStatusText: error?.response?.statusText,
        request: error?.request,
        config: error?.config
      });
      console.error('❌ [AddReceipt] Error occurred with parameters:', {
        CustomerId: form.CustomerId,
        BusinessId: form.BusinessId,
        BranchId: form.BranchId
      });
      Swal.fire("Error", "Failed to load credit invoices", "error");
    } finally {
      setLoading(false);
      console.log('🏁 [AddReceipt] loadCreditInvoices completed');
    }
  };

  const searchCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const cleanedInput = inputValue.replace(/\s+/g, '');
      const response = await searchCustomers(cleanedInput, 1, 20);

      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));

      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  // Auto-allocate payment using FIFO (oldest invoice first)
  const allocatePayment = (amount) => {
    if (!creditInvoices || creditInvoices.length === 0) {
      setPaymentAllocation([]);
      return;
    }

    let remainingAmount = amount;
    const allocation = [];

    // Sort invoices by date (oldest first)
    // API returns: invoice_Id, invoice_No, invoice_Date, invoice_NetAmount, paidAmount, outstandingAmount
    const sortedInvoices = [...creditInvoices].sort((a, b) =>
      new Date(a.invoice_Date || a.Invoice_Date) - new Date(b.invoice_Date || b.Invoice_Date)
    );

    for (const invoice of sortedInvoices) {
      if (remainingAmount <= 0) break;

      // Handle both camelCase and PascalCase from API
      const outstanding = invoice.outstandingAmount || invoice.OutstandingAmount || 0;
      const paidInThisReceipt = Math.min(remainingAmount, outstanding);
      // Round to 2 decimal places to avoid floating point precision issues
      const paidAmountRounded = Math.round(paidInThisReceipt * 100) / 100;
      const previousPaid = invoice.paidAmount || invoice.PaidAmount || 0;
      const settledAfter = previousPaid + paidAmountRounded;
      const invoiceId = invoice.invoice_Id || invoice.Invoice_Id;
      const invoiceNo = invoice.invoice_No || invoice.Invoice_No;
      const invoiceDate = invoice.invoice_Date || invoice.Invoice_Date;
      const invoiceNetAmount = invoice.invoice_NetAmount || invoice.Invoice_NetAmount;

      allocation.push({
        InvoiceId: invoiceId,
        Invoice_No: invoiceNo,
        Invoice_Date: invoiceDate,
        InvoiceAmount: invoiceNetAmount,
        OutstandingBefore: outstanding,
        PaidAmount: paidAmountRounded,
        OutstandingAfter: Math.round((outstanding - paidAmountRounded) * 100) / 100,
        TotalAmount: outstanding, // Outstanding before this payment
        SettledAmount: Math.round(settledAfter * 100) / 100, // Cumulative settled after this payment
      });

      remainingAmount -= paidAmountRounded;
    }

    setPaymentAllocation(allocation);
  };

  // Update allocation amount manually
  const updateAllocationAmount = (index, newAmount) => {
    const allocation = [...paymentAllocation];
    const item = allocation[index];
    const maxAmount = item.OutstandingBefore;

    // Round to 2 decimal places to avoid floating point precision issues
    const roundedAmount = Math.round(newAmount * 100) / 100;

    if (roundedAmount > maxAmount) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Amount',
        text: `Amount cannot exceed outstanding amount (${maxAmount.toFixed(2)})`,
      });
      return;
    }

    item.PaidAmount = roundedAmount;
    item.OutstandingAfter = Math.round((item.OutstandingBefore - roundedAmount) * 100) / 100;
    const invoice = creditInvoices.find(inv => (inv.invoice_Id || inv.Invoice_Id) === item.InvoiceId);
    const previousPaid = invoice ? (invoice.paidAmount || invoice.PaidAmount || 0) : 0;
    item.SettledAmount = Math.round((previousPaid + roundedAmount) * 100) / 100;

    // Recalculate remaining allocation
    const totalAllocated = allocation.reduce((sum, a) => sum + a.PaidAmount, 0);
    // Use tolerance of 0.01 for floating point comparison
    if (totalAllocated > form.Amount + 0.01) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Amount',
        text: `Total allocated amount (${totalAllocated.toFixed(2)}) cannot exceed payment amount (${form.Amount.toFixed(2)})`,
      });
      return;
    }

    setPaymentAllocation(allocation);
  };

  const handleFormChange = (field, value) => {
    console.log('📝 [AddReceipt] handleFormChange called:', {
      field,
      value,
      valueType: typeof value,
      isCustomerId: field === 'CustomerId',
      previousCustomerId: form.CustomerId
    });

    setForm(prev => {
      const newForm = { ...prev, [field]: value };

      // Log when CustomerId changes
      if (field === 'CustomerId') {
        console.log('👤 [AddReceipt] CustomerId changed:', {
          oldValue: prev.CustomerId,
          newValue: value,
          newFormState: {
            CustomerId: newForm.CustomerId,
            BusinessId: newForm.BusinessId,
            BranchId: newForm.BranchId
          }
        });
      }

      return newForm;
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file (Images and PDFs only, max 5MB, no video)
      const validation = validateFile(file, { 
        maxSizeMB: 5, 
        allowedTypes: ['image/jpeg', 'image/png', 'application/pdf'] 
      });

      if (!validation.valid) {
        // Reset file input if validation fails
        e.target.value = "";
        return;
      }

      handleFormChange("Receipt_Document", file);
    }
  };

  const handleSave = async () => {
    // Prevent double-click / repeated save
    if (saveLoading) return;
    setSaveLoading(true);
    try {
      // Validation
      if (!form.BusinessId) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }
      if (!form.CustomerId) {
        Swal.fire("Error", "Please select a customer", "error");
        return;
      }
      if (!form.Amount || form.Amount <= 0) {
        Swal.fire("Error", "Please enter a valid payment amount", "error");
        return;
      }
      if (!form.PaymentType) {
        Swal.fire("Error", "Please select a payment method", "error");
        return;
      }

      // Check for overpayment
      const totalAllocated = paymentAllocation.reduce((sum, a) => sum + a.PaidAmount, 0);
      const totalOutstanding = creditInvoices.reduce((sum, inv) => sum + inv.OutstandingAmount, 0);

      if (creditInvoices.length > 0 && totalAllocated > totalOutstanding) {
        Swal.fire({
          icon: 'error',
          title: 'Overpayment Detected',
          text: `Payment amount (${totalAllocated.toFixed(2)}) exceeds total outstanding amount (${totalOutstanding.toFixed(2)}). Please adjust the payment allocation.`,
        });
        return;
      }

      // Check if payment amount matches allocation
      if (creditInvoices.length > 0 && Math.abs(totalAllocated - form.Amount) > 0.01) {
        Swal.fire({
          icon: 'warning',
          title: 'Amount Mismatch',
          text: `Total allocated amount (${totalAllocated.toFixed(2)}) does not match payment amount (${form.Amount.toFixed(2)}). Please adjust the allocation.`,
        });
        return;
      }

      // Check day closing before saving (use stricter validation)
      if (form.BusinessId && form.ReceiptDate) {
        try {
          const receiptDate = new Date(form.ReceiptDate);
          const receiptDateOnly = new Date(receiptDate);
          receiptDateOnly.setHours(0, 0, 0, 0);

          // Use stricter validation with previous days check
          const checkRequest = {
            transactionDate: formatDateForAPI(receiptDateOnly, false),
            businessId: form.BusinessId,
            branchId: form.BranchId || null,
            checkPreviousDays: true  // Enable stricter validation
          };

          const dayClosingCheck = await checkDayClosing(checkRequest);

          // Check if selected date itself is closed
          if (dayClosingCheck.isClosed) {
            const message = dayClosingCheck.isHoliday
              ? `Holiday has been marked for ${formatDateForDisplay(receiptDateOnly, 'date')}. Please select a different date.`
              : `Day closing has been marked for ${formatDateForDisplay(receiptDateOnly, 'date')}. Please select a different date.`;
            Swal.fire("Day Closed", message, "warning");
            return;
          }

          // Check if any previous days are not closed (stricter validation)
          if (dayClosingCheck.firstUnclosedDate) {
            const unclosedDateStr = formatDateForDisplay(new Date(dayClosingCheck.firstUnclosedDate), 'date');
            Swal.fire("Previous Day Not Closed", `Day closing is not done for ${unclosedDateStr}. Please close that day first before creating transactions for ${formatDateForDisplay(receiptDateOnly, 'date')}.`, "warning");
            return;
          }
        } catch (error) {
          console.error("Error checking day closing:", error);
          // Continue with save if check fails (don't block due to API error)
        }
      }

      // Combine selected date with current time (client's local timezone)
      // This ensures the time reflects when the user is creating the receipt in their timezone
      const selectedDate = form.ReceiptDate instanceof Date ? form.ReceiptDate : new Date(form.ReceiptDate);
      const now = new Date();
      const receiptDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      // Always use FormData to ensure ReceiptDetails is properly sent
      const formData = new FormData();
      formData.append('ReceiptId', form.ReceiptId);
      formData.append('ReceiptNumber', form.ReceiptNumber);
      formData.append('ReceiptDate', formatDateForAPI(receiptDateTime, true));
      formData.append('Amount', form.Amount);
      formData.append('PaymentType', form.PaymentType);
      if ((form.PaymentType === "cc" || form.PaymentType === "on") && form.AuthCode) {
        formData.append('AuthCode', form.AuthCode);
      }
      formData.append('Type', "cu");
      formData.append('Is_Advance', creditInvoices.length === 0 || paymentAllocation.length === 0);
      formData.append('BusinessId', form.BusinessId);
      if (form.BranchId) formData.append('BranchId', form.BranchId);
      formData.append('CustomerId', form.CustomerId);
      formData.append('CreatedBy', session.userId);

      // Always send ReceiptDetails as JSON string (using receiptDetailsJson to avoid conflict with Receipt.ReceiptDetails property)
      formData.append('receiptDetailsJson', JSON.stringify(paymentAllocation.map(a => ({
        InvoiceId: a.InvoiceId,
        InvoiceAmount: Math.round((a.InvoiceAmount || 0) * 100) / 100,
        PaidAmount: Math.round((a.PaidAmount || 0) * 100) / 100,
        TotalAmount: Math.round((a.TotalAmount || 0) * 100) / 100,
        SettledAmount: Math.round((a.SettledAmount || 0) * 100) / 100,
      }))));

      // Only append documentFile if a document is selected
      if (form.Receipt_Document) {
        formData.append('documentFile', form.Receipt_Document);
      }

      const receiptData = formData;

      const result = await saveReceipt(receiptData);

      if (result.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: result.message || 'Receipt saved successfully',
        });

        // Reset form
        setForm({
          ...defaultForm,
          BusinessId: session.companyId || null,
          BranchId: session.branchId || null,
          ReceiptDate: new Date(),
        });
        setPaymentAllocation([]);
        setCreditInvoices([]);
        setSelectedCustomerOption(null);
        loadNextReceiptNumber();

        // Reset file input
        const fileInput = document.getElementById('receiptDocumentUpload');
        if (fileInput) fileInput.value = '';
      } else {
        Swal.fire("Error", result.message || "Failed to save receipt", "error");
      }
    } catch (error) {
      console.error("Error saving receipt:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save receipt";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name,
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    if (!form.BusinessId) return [];
    return branches
      .filter(b => b.branch_Buisness_Id === form.BusinessId)
      .map(b => ({
        value: b.branch_Id,
        label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim(),
      }));
  }, [branches, form.BusinessId]);

  const paymentMethodOptions = [
    { value: "ca", label: "Cash" },
    { value: "cc", label: "Credit Card" },
    { value: "on", label: "Online" },
  ];

  const formatDate = (date) => {
    return formatDateForDisplay(date, 'date');
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Add Receipt" breadcrumbItem="Add Receipt" />

          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  {/* Form Section */}
                  {hasEditOrHigher && (
                    <>
                      <h5 className="card-title mb-4">Enter Payment</h5>
                      <Form>
                        <Row className="mb-4">
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Company <span className="text-danger">*</span></Label>
                              <Select
                                value={businessOptions.find(opt => opt.value === form.BusinessId) || null}
                                onChange={(option) => {
                                  handleFormChange("BusinessId", option?.value || null);
                                  handleFormChange("BranchId", null);
                                }}
                                options={businessOptions}
                                isClearable={!isBusinessReadonly}
                                isDisabled={isBusinessReadonly || isReadOnly}
                                placeholder="Select Company"
                                maxMenuHeight={200}
                                menuPlacement="auto"
                                isSearchable
                                styles={selectStyles}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Branch</Label>
                              <Select
                                value={branchOptions.find(opt => opt.value === form.BranchId) || null}
                                onChange={(option) => handleFormChange("BranchId", option?.value || null)}
                                options={branchOptions}
                                isClearable={!isBranchReadonly}
                                isDisabled={isBranchReadonly || !form.BusinessId || isReadOnly}
                                placeholder="Select Branch"
                                maxMenuHeight={200}
                                menuPlacement="auto"
                                isSearchable
                                styles={selectStyles}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Receipt Number</Label>
                              <Input
                                type="text"
                                value={form.ReceiptNumber}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Receipt Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.ReceiptDate}
                                onChange={(date) => handleFormChange("ReceiptDate", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={new Date()}
                              // Keep date picker enabled even when form is disabled (so user can change date after selecting closed day)
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="mb-4">
                          <Col md={6}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Customer <span className="text-danger">*</span></Label>
                              <div className="d-flex gap-2">
                                <div style={{ flex: 1 }}>
                                  <div className="d-flex gap-2">
                                    <div style={{ flex: 1 }}>
                                      <Select
                                        value={selectedCustomerOption}
                                        onChange={(option) => {
                                          console.log('🔽 [AddReceipt] Customer dropdown onChange:', {
                                            option,
                                            customerId: option?.value,
                                            customerLabel: option?.label,
                                            customerData: option?.customer
                                          });
                                          setSelectedCustomerOption(option);
                                          handleFormChange("CustomerId", option?.value || null);
                                        }}
                                        options={customerSearchOptions}
                                        placeholder="Type at least 3 characters to search customers..."
                                        maxMenuHeight={200}
                                        menuPlacement="auto"
                                        isSearchable
                                        styles={selectStyles}
                                        isClearable
                                        isLoading={customerSearchLoading}
                                        isDisabled={isReadOnly}
                                        noOptionsMessage={() => "Type at least 3 characters to search"}
                                        loadingMessage={() => "Searching customers..."}
                                        filterOption={() => true}
                                        onInputChange={(inputValue) => {
                                          if (isReadOnly) return inputValue;
                                          if (searchTimeout) {
                                            clearTimeout(searchTimeout);
                                          }
                                          const timeout = setTimeout(() => {
                                            searchCustomersManually(inputValue);
                                          }, 300);
                                          setSearchTimeout(timeout);
                                          return inputValue;
                                        }}
                                      />
                                    </div>
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => setShowAdvancedSearchModal(true)}
                                      disabled={isReadOnly}
                                      title="Advanced Customer Search"
                                      style={{ height: '38px' }}
                                    >
                                      <Search size={16} />
                                    </Button>
                                  </div>
                                </div>
                                {selectedCustomerOption?.value && (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => setShowCustomerProfile(true)}
                                    disabled={isReadOnly}
                                    title="View Customer Profile"
                                  >
                                    <User size={16} />
                                  </Button>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Credit Invoices Table */}
                        {creditInvoices.length > 0 && (
                          <Row className="mt-3">
                            <Col xs={12}>
                              <h6 className="mb-2">Credit Invoices</h6>
                              <div className="table-responsive">
                                <Table bordered size="sm">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Invoice No</th>
                                      <th>Date</th>
                                      <th>Net Amount</th>
                                      <th>Paid Amount</th>
                                      <th>Outstanding</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {creditInvoices.map((inv) => {
                                      // Handle both camelCase and PascalCase from API
                                      const invoiceId = inv.invoice_Id || inv.Invoice_Id;
                                      const invoiceNo = inv.invoice_No || inv.Invoice_No || 'N/A';
                                      const invoiceDate = inv.invoice_Date || inv.Invoice_Date;
                                      const netAmount = inv.invoice_NetAmount || inv.Invoice_NetAmount || 0;
                                      const paidAmount = inv.paidAmount || inv.PaidAmount || 0;
                                      const outstanding = inv.outstandingAmount || inv.OutstandingAmount || 0;

                                      return (
                                        <tr key={invoiceId}>
                                          <td>{invoiceNo}</td>
                                          <td>{formatDate(invoiceDate)}</td>
                                          <td>{formatCurrency(netAmount)}</td>
                                          <td>{formatCurrency(paidAmount)}</td>
                                          <td><strong>{formatCurrency(outstanding)}</strong></td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </Col>
                          </Row>
                        )}

                        {creditInvoices.length === 0 && form.CustomerId && (
                          <Row className="mt-3">
                            <Col xs={12}>
                              <div className="alert alert-info">
                                No outstanding credit invoices found for this customer.
                                This payment will be recorded as an advance.
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Row className="mb-4">
                          <Col md={4}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Payment Amount <span className="text-danger">*</span></Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.Amount || ''}
                                onChange={(e) => handleFormChange("Amount", parseFloat(e.target.value) || 0)}
                                placeholder="Enter payment amount"
                                disabled={isReadOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Payment Method <span className="text-danger">*</span></Label>
                              <Select
                                value={paymentMethodOptions.find(opt => opt.value === form.PaymentType) || null}
                                onChange={(option) => handleFormChange("PaymentType", option?.value || "ca")}
                                options={paymentMethodOptions}
                                maxMenuHeight={200}
                                menuPlacement="auto"
                                styles={selectStyles}
                                isDisabled={isReadOnly}
                              />
                            </FormGroup>
                          </Col>
                          {(form.PaymentType === "cc" || form.PaymentType === "on") && (
                            <Col md={4}>
                              <FormGroup className="mb-0">
                                <Label className="mb-2">Authentication Code</Label>
                                <Input
                                  type="text"
                                  value={form.AuthCode}
                                  onChange={(e) => handleFormChange("AuthCode", e.target.value)}
                                  placeholder="Enter transaction/reference number"
                                />
                              </FormGroup>
                            </Col>
                          )}
                        </Row>

                        <Row className="mb-4">
                          <Col md={6}>
                            <FormGroup className="mb-0">
                              <Label className="mb-2">Receipt Document</Label>
                              <input
                                key={form.Receipt_Document ? form.Receipt_Document.name : 'file-input'}
                                id="receiptDocumentUpload"
                                type="file"
                                className="d-none"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={handleFileChange}
                                disabled={isReadOnly}
                              />
                              <div className="input-group">
                                <label className="input-group-text mb-0" htmlFor="receiptDocumentUpload" style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}>
                                  <FileText size={16} className="me-2" />
                                  Choose File
                                </label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={form.Receipt_Document ? form.Receipt_Document.name : (form.Receipt_DocumentPath ? form.Receipt_DocumentPath.split("/").pop() : "")}
                                  readOnly
                                  placeholder="No file chosen"
                                />
                              </div>
                              {form.Receipt_DocumentPath && !form.Receipt_Document && (
                                <small className="text-muted d-block mt-1">
                                  Current: {form.Receipt_DocumentPath.split("/").pop()}
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Payment Allocation Table */}
                        {paymentAllocation.length > 0 && (
                          <Row className="mt-3">
                            <Col xs={12}>
                              <h6 className="mb-2">Payment Allocation</h6>
                              <div className="table-responsive">
                                <Table bordered size="sm">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Invoice No</th>
                                      <th>Outstanding</th>
                                      <th>Allocated Amount</th>
                                      <th>Remaining</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paymentAllocation.map((alloc, index) => (
                                      <tr key={alloc.InvoiceId}>
                                        <td>{alloc.Invoice_No}</td>
                                        <td>{formatCurrency(alloc.OutstandingBefore)}</td>
                                        <td>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max={alloc.OutstandingBefore}
                                            value={alloc.PaidAmount || ''}
                                            onChange={(e) => updateAllocationAmount(index, parseFloat(e.target.value) || 0)}
                                            style={{ width: '120px' }}
                                          />
                                        </td>
                                        <td>{formatCurrency(alloc.OutstandingAfter)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr>
                                      <th colSpan="2">Total Allocated</th>
                                      <th>{formatCurrency(paymentAllocation.reduce((sum, a) => sum + a.PaidAmount, 0))}</th>
                                      <th></th>
                                    </tr>
                                  </tfoot>
                                </Table>
                              </div>
                            </Col>
                          </Row>
                        )}

                        <Row className="mt-4 mb-0">
                          <Col>
                            <Button
                              color="primary"
                              onClick={handleSave}
                              disabled={saveLoading || !form.CustomerId || !form.BusinessId || isReadOnly}
                            >
                              {saveLoading && <ClipLoader size={16} color="#ffffff" className="me-2" />}
                              Save Receipt
                            </Button>
                            <Button
                              color="secondary"
                              className="ms-2"
                              onClick={() => {
                                setForm({
                                  ...defaultForm,
                                  BusinessId: session.companyId || null,
                                  BranchId: session.branchId || null,
                                  ReceiptDate: new Date(),
                                });
                                setPaymentAllocation([]);
                                setCreditInvoices([]);
                                setSelectedCustomerOption(null);
                                loadNextReceiptNumber();

                                // Reset file input
                                const fileInput = document.getElementById('receiptDocumentUpload');
                                if (fileInput) fileInput.value = '';
                              }}
                            >
                              Clear
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </>
                  )}
                  {!hasEditOrHigher && (
                    <div className="alert alert-warning">
                      You don't have permission to add receipts. Please contact your administrator.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Customer Profile Modal */}
        <CustomerProfileModal
          isOpen={showCustomerProfile}
          toggle={() => setShowCustomerProfile(false)}
          customerId={selectedCustomerOption?.value || null}
          customer={selectedCustomerOption ? { customer_Id: selectedCustomerOption.value } : null}
        />

        {/* Advanced Customer Search Modal */}
        <CustomerAdvancedSearchModal
          isOpen={showAdvancedSearchModal}
          toggle={() => setShowAdvancedSearchModal(false)}
          userBranchId={session.branchId}
          onCustomerSelected={(customer) => {
            console.log('🔍 [AddReceipt] Customer selected from advanced search:', {
              customer,
              customerId: customer.customer_Id,
              customerName: customer.customer_NameEn,
              customerContact: customer.customer_ContactNo,
              currentFormState: {
                CustomerId: form.CustomerId,
                BusinessId: form.BusinessId,
                BranchId: form.BranchId
              }
            });

            // Set the selected customer in the form and dropdown
            setForm(prev => {
              const newForm = {
                ...prev,
                CustomerId: customer.customer_Id
              };
              console.log('📝 [AddReceipt] Form updated with new customer:', {
                previousCustomerId: prev.CustomerId,
                newCustomerId: newForm.CustomerId,
                newFormState: newForm
              });
              return newForm;
            });

            setSelectedCustomerOption({
              value: customer.customer_Id,
              label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
              customer: customer
            });

            console.log('📋 [AddReceipt] selectedCustomerOption set:', {
              value: customer.customer_Id,
              label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`
            });

            // Load credit invoices for the selected customer
            if (customer.customer_Id) {
              console.log('🔄 [AddReceipt] Customer ID exists, will trigger loadCreditInvoices via useEffect');
              // Note: loadCreditInvoices will be called automatically by useEffect when CustomerId changes
            } else {
              console.warn('⚠️ [AddReceipt] Customer ID is null/undefined, skipping credit invoices load');
            }
          }}
        />
      </div>
    </React.Fragment>
  );
}




